import React, { useState, useMemo } from 'react'
import {
  Box,
  Link,
  IconButton,
  Typography,
  InputAdornment,
} from '@mui/material'
import { CommonTextField, CommonButton } from '@/components/common'
import { STORAGE_KEYS } from '@/constants'
import { useDonorContext } from '@/context'
import { useLogin, usePortalConstantValue } from '@/hooks'
import { VisibilityIcon, VisibilityOffIcon } from '@/icons'
import { forcyteService, localStorageService } from '@/services'

export const Login = ({ redirectUrl, onSuccess, onAccountRecovery }) => {
  const { getDonor } = useDonorContext()
  const { loadingLogin, login } = useLogin()
  const getPortalConstantValue = usePortalConstantValue()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const headingText = useMemo(() => {
    if (redirectUrl) return 'Please Log In'
    else return getPortalConstantValue('LOGIN_PAGE_TITLE')
  }, [redirectUrl])

  const handleLogin = async e => {
    if (e) e.preventDefault()
    setError('')

    try {
      const response = await login({ email, password })
      if (response && response.token) {
        forcyteService.setToken(response.token, response.token_expiry)
        forcyteService.refresh()
        await getDonor()
        onSuccess()
      }
    } catch (error) {
      setError('Invalid email or password')
    }
  }

  const handleContinueAsGuest = () => {
    localStorageService.setItem(STORAGE_KEYS.IS_GUEST, 'true')
    localStorageService.setItem(STORAGE_KEYS.IS_GUEST_AT, Date.now().toString())
    onSuccess()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component='form'
        onSubmit={handleLogin}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: 700,
          gap: 3,
        }}
      >
        <Typography
          variant='h4'
          sx={{
            textAlign: 'center',
            mb: 2,
          }}
        >
          {headingText}
        </Typography>

        <CommonTextField
          id='email'
          label='Email'
          value={email}
          setValue={setEmail}
          type='email'
          error={error}
          placeholder={getPortalConstantValue('LOGIN_EMAIL_PLACEHOLDER')}
          inputProps={{
            name: 'email',
            autoComplete: 'username email',
          }}
        />

        <CommonTextField
          id='password'
          label='Password'
          value={password}
          setValue={setPassword}
          type={showPassword ? 'text' : 'password'}
          error={error}
          placeholder={getPortalConstantValue('LOGIN_PASSWORD_PLACEHOLDER')}
          inputProps={{
            name: 'password',
            autoComplete: 'current-password',
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={e => e.preventDefault()}
                    edge='end'
                    size='small'
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          labelRight={
            <Link
              onClick={onAccountRecovery}
              sx={{
                fontSize: '0.875rem',
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {getPortalConstantValue('LOGIN_WITHOUT_PW_DESCRIPTION')}
            </Link>
          }
        />

        <CommonButton
          id='login-button'
          label={getPortalConstantValue('LOGIN_BUTTON_TEXT')}
          type='submit'
          onClick={handleLogin}
          loading={loadingLogin}
          disabled={!email || !password}
          size='large'
          style={{ width: '100%' }}
        />

        <Typography variant='body1'>
          {getPortalConstantValue('LOGIN_PAGE_ACCOUNT_NUDGE_TEXT')} Click{' '}
          <Link sx={{ cursor: 'pointer' }} onClick={onAccountRecovery}>
            here
          </Link>
        </Typography>

        <CommonButton
          id='continue-as-guest-button'
          label={getPortalConstantValue('LOGIN_PAGE_GUEST_TEXT')}
          link
          variant='text'
          onClick={handleContinueAsGuest}
          size='small'
          sx={{ fontWeight: 400 }}
        />
      </Box>
    </Box>
  )
}
